import { ChangeDetectionStrategy, Component, inject, OnDestroy, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast/toast.service';
import { audioBufferToBlob, checkAudioStreamActivity } from '../../../helpers/audio.helper';
import { DeviceSelectorComponent } from '../../device-selector.component';

@Component({
  selector: 'app-voice-recorder-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, TranslateModule, DeviceSelectorComponent],
  templateUrl: './voice-recorder-button.component.html',
  styleUrls: ['./voice-recorder-button.component.scss'],
})
export class VoiceRecorderButtonComponent implements OnDestroy {
  /** Emits the recorded audio File when recording stops. */
  recorded = output<File>();

  /** Emits when recording state changes. */
  recordingChange = output<boolean>();

  isRecording = signal(false);
  haloScale = signal(1);
  haloOpacity = signal(0.5);
  selectedMicrophoneId = signal<string | null>(localStorage.getItem('selectedMicrophone'));
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);
  microphonePermissionState = toSignal(this.permissionWatcher.microphonePermission$, { initialValue: 'prompt' });

  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  onMicrophoneSelected(deviceId: string): void {
    this.selectedMicrophoneId.set(deviceId);
  }

  async requestPermission(): Promise<void> {
    await this.permissionWatcher.requestMicrophoneAccess();
  }

  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;

  // Audio visualization
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private dataArray: Uint8Array | null = null;
  private animationFrameId: number | null = null;

  async toggle(): Promise<void> {
    if (this.isRecording()) {
      this.stop();
    } else {
      await this.start();
    }
  }

  private async start(): Promise<void> {
    try {
      let audioConstraints: any = { echoCancellation: true };
      const selectedMic = this.selectedMicrophoneId();
      if (selectedMic && selectedMic !== 'default') {
        audioConstraints.deviceId = { exact: selectedMic };
      }

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      } catch (err) {
        console.warn('[VoiceRecorder] Failed exact microphone, falling back to ideal/default:', err);
        audioConstraints = { echoCancellation: true };
        if (selectedMic && selectedMic !== 'default') {
          audioConstraints.deviceId = { ideal: selectedMic };
        }
        stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      }

      checkAudioStreamActivity(stream).then(hasActivity => {
        if (!hasActivity) {
          this.toast.showAudioTroubleshootingDialog();
        }
      });

      this.stream = stream;
      this.mediaRecorder = new MediaRecorder(stream);
      this.chunks = [];

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          this.chunks.push(e.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        this.stream = null;

        const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
        let ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
        
        const originalBlob = new Blob(this.chunks, { type: mimeType });
        let finalBlob = originalBlob;

        try {
          const audioContext = new AudioContext();
          const arrayBuffer = await originalBlob.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          finalBlob = audioBufferToBlob(audioBuffer);
          ext = 'wav';
          if (audioContext.state !== 'closed') {
            audioContext.close();
          }
        } catch (err) {
          console.error('Failed to convert recording to WAV:', err);
        }

        const file = new File([finalBlob], `VoiceNote_${Date.now()}.${ext}`, { type: finalBlob.type });
        
        this.recorded.emit(file);
      };

      // Pass a timeslice to ensure data is periodically emitted
      this.mediaRecorder.start(100);
      this.isRecording.set(true);
      this.recordingChange.emit(true);
      this.initVisualization(stream);
    } catch (err: any) {
      console.error('Mic access denied', err);
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        this.toast.showDevicePermissionError();
      } else {
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'An error occurred', 'OK', { duration: 3000 });
      }
    }
  }

  private stop(): void {
    this.mediaRecorder?.stop();
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.isRecording.set(false);
    this.recordingChange.emit(false);
    this.stopVisualization();
  }

  // ── Audio-reactive halo ──────────────────────────

  private initVisualization(stream: MediaStream): void {
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.source = this.audioContext.createMediaStreamSource(stream);
    this.source.connect(this.analyser);
    this.draw();
  }

  private draw = () => {
    if (!this.analyser || !this.dataArray) return;
    this.animationFrameId = requestAnimationFrame(this.draw);
    this.analyser.getByteFrequencyData(this.dataArray as Uint8Array<ArrayBuffer>);

    let sum = 0;
    for (const amplitude of this.dataArray) {
      sum += amplitude * amplitude;
    }
    const average = Math.sqrt(sum / this.dataArray.length);
    const normalizedVolume = average / 128;

    this.haloScale.set(1 + normalizedVolume * 0.4);
    this.haloOpacity.set(0.3 + normalizedVolume * 0.5);
  };

  private stopVisualization(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    this.audioContext?.close().catch(console.error);
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.haloScale.set(1);
    this.haloOpacity.set(0.5);
  }

  ngOnDestroy(): void {
    this.stopVisualization();
    if (this.isRecording() && this.mediaRecorder) {
      this.mediaRecorder.stop();
    }
    this.stream?.getTracks().forEach((track) => track.stop());
  }
}
